import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, FormControl, InputLabel, MenuItem, Paper, Select, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HistoryIcon from '@mui/icons-material/History';
import { motion } from 'motion/react';
import {
  getIpamConfig, getIpamPoolTree, listIpamInventoryAllocations, listIpamMembers,
} from '../../../../services/ipamApi';
import PortalPageHeader from '../../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalEmptyState, PortalErrorState, PortalSectionHeader,
} from '../../../../components/portal/PortalStates';
import { pageVariants, sectionVariants } from '../../../../animations/variants';
import { formatStatusLabel } from '../../../../utils/statusLabel';
import {
  CK, FONT_BODY, FONT_MONO, bodyCellSx, cidrCellSx, headCellSx, rowSx, sectionCardSx,
  statusChipSx, tableHeadRowSx,
} from '../../portalStyles';
import IpamDiscoveryColdStart, { IPAM_DISCOVERY_POLL_MS } from './IpamDiscoveryColdStart';
import IpamHistoryDrawer from './IpamHistoryDrawer';
// IMPORTED, NEVER RETYPED. The zero state on this page states what AWS reported,
// and that sentence is spelled once — see ipamCopy.js.
import {
  IPAM_LIVENESS_UNREAD_BODY, IPAM_LIVENESS_UNREAD_TITLE,
  ipamAllocationsZeroState, ipamBlockingAllocationsChip, ipamConfigNoteSeverity,
  ipamConfigNoteTitle, ipamResidualIpamCard,
} from './ipamCopy';

// IpamAllocations — the cross-pool ledger of what IPAM HANDED OUT.
//
// DISTINCT FROM ADDRESS SPACE, AND THE DISTINCTION IS THE PRODUCT. Address Space
// is what IPAM DISCOVERED: every CIDR sitting on a real resource in an enrolled
// account, whether IPAM had anything to do with it or not. This page is only what
// IPAM allocated out of its own pools. A VPC created outside a pool appears on
// Address Space and NOT here, and that delta is the entire compliance story — it
// is address space nothing is planning around. Read as two views of one dataset,
// both pages are misread, so the banner below says it on screen.
//
// THIS IS ALSO NOT `GET /pools/{poolId}/allocations`. That is one pool's rows
// hanging off an {id} lookup, which 404s for a missing pool. This is
// GET /inventory/allocations — every pool's rows in one keyset-paginated feed,
// answering an empty page rather than an error.
//
// THE ROWS INCLUDE ALLOCATIONS PRISM DID NOT MAKE. models.IpamPoolAllocation
// caches everything AWS reports, including a member account allocating out of a
// pool shared to it. Prism cannot release those — but they still block
// DeleteIpamPool, which is why the owning {resourceId, accountId} is on the row
// and not hidden behind a detail click.

const IPAM_COLOR = '#455A64';

const ADDRESS_SPACE_ROUTE = '/securityhub/ipam/address-space';
const POOLS_ROUTE = '/securityhub/ipam/pools';
const ACCOUNTS_ROUTE = '/securityhub/ipam/accounts';
const IPAM_ROOT_ROUTE = '/securityhub/ipam';

const FILTER_DEBOUNCE_MS = 350;
const MAX_POLL_FAILURES = 3;

// --- Is there an IPAM behind this config row? --------------------------------
//
// A CONFIG ROW IS NOT AN IPAM. One exists from the moment BeginIpamConfig runs —
// writeDraftConfig creates it at `awaiting_consent` — and a `failed` row persists
// forever afterwards. The sidebar renders all seven IPAM links whether or not the
// capability was ever enabled, so this page is directly reachable in every one of
// those states.
//
// `enabled` and `partial_failure` are the ONLY two statuses at which an IPAM
// exists and AWS's sweep is the thing being waited on. They are also exactly the
// pair StartRefresh accepts (services/ipam_service.go: "cannot refresh while VPC
// IPAM is %s"), which is what makes this the right predicate rather than a
// second, looser opinion about the same question.
const ipamIsLive = (status) => status === 'enabled' || status === 'partial_failure';

// The copy for a config row with NO live IPAM behind it and no sweep ever
// completed. Every one of these states used to render the cold-start panel —
// "AWS is still taking its first inventory… Nothing is wrong and there is nothing
// to retry" — under an indeterminate bar that will never finish, above a "Check
// for results" button whose refresh the backend refuses because the config is not
// settled. There is no IPAM, no discovery and no association: nothing will ever
// arrive, and each state has a different way out, so each one says its own.
const NOT_LIVE_META = {
  awaiting_consent: {
    title: 'VPC IPAM setup was never finished',
    description: 'Setup was started but stopped at the cost estimate, so no IPAM and no pools were '
      + 'ever created. Nothing has been allocated and nothing is on its way — finish the setup '
      + 'wizard to start planning your address space.',
  },
  in_progress: {
    title: 'VPC IPAM is still being set up',
    description: 'Prism is still creating the IPAM and enrolling accounts, and AWS has not '
      + 'completed a discovery sweep yet. There is nothing to show here until it does.',
  },
  failed: {
    title: 'VPC IPAM setup failed',
    description: 'Setup did not finish, so there is no IPAM and there are no pools to allocate '
      + 'from. Nothing is running and nothing will resume on its own — re-run the setup wizard.',
  },
  blocked: {
    title: 'A VPC IPAM teardown is waiting on you',
    description: 'A teardown stopped part-way because member accounts still hold live '
      + 'allocations, and this IPAM never completed a discovery sweep, so there is no ledger to '
      + 'show. The IPAM is still there and still billing until it is deleted.',
  },
  disabled: {
    title: 'VPC IPAM is not enabled yet',
    description: 'There are no pools, so nothing has been allocated. Enable VPC IPAM to start '
      + 'planning your address space.',
  },
};

const notLiveMeta = (status) => NOT_LIVE_META[status] || NOT_LIVE_META.disabled;

// Is there anything for this page to draw at all?
//
// `in_progress` and `blocked` are the two statuses that are not live and can
// still have a real IPAM behind them — an operation running over a working one,
// and a teardown parked on one that is still there and still billing — so those
// two are judged on whether a sweep has ever landed, and fall through to the
// ledger when it has.
//
// EVERY OTHER NON-LIVE STATUS IS JUDGED ON THE STATUS ALONE, deliberately
// ignoring firstDiscoveryAt. That timestamp "IS SET ONCE AND NEVER RESET"
// (services/ipam_service.go, writeRefreshFreshness) and BeginIpamConfig upserts
// the existing row, so a tenant who tore an IPAM down and started the wizard
// again sits at `awaiting_consent` still carrying the old one. Reading it as
// "this tenant has allocated before" would put stale rows under a live-looking
// ledger for a tenant with no IPAM and no pools at all.
const ipamHasNothingToShow = (config) => {
  if (!config) return true;
  if (ipamIsLive(config.status)) return false;
  if (config.status === 'in_progress' || config.status === 'blocked') {
    return !config.firstDiscoveryAt;
  }
  return true;
};

// THE THIRD ANSWER, PROPAGATED FROM IpamPools.js's ipamLiveness. A config row
// carrying NO STATUS AT ALL is not a decided negative — it is a field Prism never
// read — and without this arm NOT_LIVE_META's `disabled` fallback dresses the
// blank as "VPC IPAM is not enabled yet" and points an operator whose IPAM may be
// running and billing at the setup wizard.
const ipamStatusUnread = (config) => !!config && !config.status;

// --- The one member filter ---------------------------------------------------
//
// SHARED WITH IpamDashboard.js, DELIBERATELY IDENTICAL. `disabled` is a
// tombstone and `removing` is a departure already in flight; neither is an
// account whose coverage could be missing, so neither belongs in any denominator
// on this page.
//
// It is load-bearing rather than tidy. ListMembers returns tombstones on purpose
// ("TOMBSTONES ARE RETURNED, NOT FILTERED OUT"), removeMember tombstones by
// writing {"phase", "error_message"} and nothing else, and syncIpamLifecycleStates
// skips `phase = disabled` cells — so an account removed BECAUSE its discovery was
// not-found keeps `resource_discovery_status = 'not-found'` forever. Without this
// filter the amber banner above names an account that is no longer enrolled, and
// goes on naming it permanently, while the Dashboard and Accounts pages correctly
// show nothing.
const isLiveMember = (m) => m?.phase !== 'disabled' && m?.phase !== 'removing';

// --- Response readers -------------------------------------------------------
//
// /inventory/allocations answers ONE envelope, fixed by services.IpamInventoryPage
// and asserted by the backend's own tests:
//
//   {"items": [...], "nextCursor": ""}
//
// `items` is NEVER null on a successful read — the un-enabled tenant and the
// cold-start tenant both answer `{"items": [], "nextCursor": ""}` — and
// `nextCursor` is always present, "" meaning the walk is exhausted.
//
// The reader is therefore INTOLERANT on purpose. Accepting a bare array or three
// candidate key names was right while the route answered 501 and nothing pinned
// the wrapper; it is wrong now, because a reader that accepts four shapes cannot
// tell a renamed key from a normal response, and the failure it hides is this
// table rendering EMPTY — which on this page reads as "no CIDR was ever allocated
// from a pool". A broken envelope becomes a visible error instead.
const ENVELOPE_ERROR = 'Prism could not read the allocation ledger: the inventory service answered '
  + 'in a shape this page does not recognise. This is a defect, not an empty ledger — the table is '
  + 'deliberately not being drawn as "nothing allocated".';

const inventoryPage = (data) => {
  if (!data || typeof data !== 'object'
    || !Array.isArray(data.items) || typeof data.nextCursor !== 'string') {
    throw new Error(ENVELOPE_ERROR);
  }
  return data;
};

// /members and /pools are NOT keyset lists and NOT that envelope: their handler
// (ipamOkOrEmptyPage) answers a bare array, and `[]` for a tenant with no config
// at all. Kept as its own reader so the two contracts cannot drift back into one
// tolerant helper that accepts either.
const bareList = (data) => (Array.isArray(data) ? data : []);

// AllocationSource is Prism-derived at ingest and is the most consequential
// derived column in the capability (models/ipam_models.go:1046-1073): it decides
// whether teardown may call ReleaseIpamPoolAllocation at all.
//
// `resource` is drawn in the ATTENTION colour rather than a neutral one, because
// it is the row that will block a teardown — and the operator who meets that
// blocker for the first time in the middle of a teardown has met it too late.
const SOURCE_META = {
  manual: {
    intent: 'info',
    label: 'Manual',
    hint: 'A reservation made directly in IPAM, backed by no AWS resource. Prism can release it, and it will not block teardown.',
  },
  resource: {
    intent: 'warning',
    label: 'Resource-backed',
    hint: 'Held by a live AWS resource. It cannot be released while that resource exists — there is no force-release API — so it will block deleting the pool, and the resource has to go first.',
  },
};

const sourceMeta = (v) => SOURCE_META[String(v || '').toLowerCase()] || {
  intent: 'warning',
  label: formatStatusLabel(v) || 'Unknown',
  // An unmapped value means AWS grew a ResourceType nobody has classified. It is
  // shown as-is and in the attention colour rather than folded into "manual",
  // because guessing "manual" here would tell an operator a teardown will succeed
  // when it may not.
  hint: 'Prism does not recognise this allocation source. Treat it as resource-backed — that is the conservative reading — until it is classified.',
};

const parseRegions = (json) => {
  if (Array.isArray(json)) return json;
  if (!json) return [];
  try {
    const v = JSON.parse(json);
    return Array.isArray(v) ? v : [];
  } catch (_) {
    return [];
  }
};

const formatTime = (iso) => {
  if (!iso) return null;
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? null : t.toLocaleString();
};

const optionsFrom = (rows, key, current) => {
  const seen = new Set();
  rows.forEach((r) => {
    const v = r?.[key];
    if (v) seen.add(v);
  });
  if (current) seen.add(current);
  return [...seen].sort();
};

const plural = (n, one, many) => (n === 1 ? one : many);

export default function IpamAllocations() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [config, setConfig] = useState(null);
  const [members, setMembers] = useState([]);
  const [pools, setPools] = useState([]);
  const [contextLoaded, setContextLoaded] = useState(false);

  // THE OUTCOME OF THE CONFIG READ, AS ITS OWN VALUE — 'pending' | 'ok' | 'failed'.
  //
  // `config === null` is the same null for "the read has not returned", "the read
  // rejected" and "the read returned the route's real answer of no configuration",
  // and `error` used to be SHARED with the ledger read — which is what produced
  // the live defect. loadContext() rejects, sets `error`, leaves `config` null;
  // 350 ms later the debounced load('') succeeds and calls setError(null);
  // `noIpamYet` then reads true and the page tells a customer whose IPAM is
  // running and billing that it is not enabled, taking the coverage banner with
  // it. Same tri-state as IpamPublicIPs.js and IpamAccounts.js.
  const [configRead, setConfigRead] = useState('pending');

  const [rows, setRows] = useState([]);
  const [nextCursor, setNextCursor] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // `error` belongs to the CONTEXT read and to nothing else; the ledger feed has
  // its own `tableError`. A successful inventory page can no longer clear an
  // error the config read owns.
  const [error, setError] = useState(null);
  const [tableError, setTableError] = useState(null);
  const [checking, setChecking] = useState(false);

  const [pool, setPool] = useState(searchParams.get('pool') || '');
  const [account, setAccount] = useState(searchParams.get('account') || '');
  const [region, setRegion] = useState(searchParams.get('region') || '');
  const [source, setSource] = useState(searchParams.get('source') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');

  const [historyRow, setHistoryRow] = useState(null);

  // MONOTONIC REQUEST GUARD — NOT OPTIONAL. The 350 ms debounce suppresses
  // keystrokes but does not order in-flight responses, and every filter change
  // restarts the keyset walk at page one. Without this guard a slow early request
  // resolves after a fast later one and the table shows results for a query the
  // user has already moved on from.
  const reqSeq = useRef(0);
  const pollFailuresRef = useRef(0);

  const loadContext = useCallback(async ({ quiet = false } = {}) => {
    try {
      const [cfgRes, memberRes, poolRes] = await Promise.all([
        getIpamConfig(), listIpamMembers(), getIpamPoolTree(),
      ]);
      setConfig(cfgRes?.data ?? null);
      setMembers(bareList(memberRes?.data));
      setPools(bareList(poolRes?.data));
      pollFailuresRef.current = 0;
      setConfigRead('ok');
      if (!quiet) setError(null);
      return true;
    } catch (e) {
      pollFailuresRef.current += 1;
      // Conservative: the three reads share one try, so a members or pool-tree
      // failure also marks the config unread. Erring towards "we do not know"
      // costs a retry; erring the other way is a claim about a customer's setup
      // built out of a rejected request.
      //
      // The FLAG is set on every rejection, quiet or not. `error` stays suppressed
      // for the first two quiet poll failures so a transient 502 does not throw a
      // red card over a working page — but "quiet" must never mean "and therefore
      // the config read succeeded".
      setConfigRead('failed');
      if (!quiet || pollFailuresRef.current >= MAX_POLL_FAILURES) {
        setError(e?.message || 'Could not read the VPC IPAM configuration.');
      }
      return false;
    } finally {
      setContextLoaded(true);
    }
  }, []);

  // No `limit`: the clamp is server-side (services.shClampFindingsLimit) and a
  // second spelling of it in a page is how a row ceiling ships.
  const load = useCallback(async (from) => {
    const myReq = ++reqSeq.current;
    if (from) setLoadingMore(true);
    else setLoading(true);
    try {
      const res = await listIpamInventoryAllocations(from ? { cursor: from } : {});
      if (myReq !== reqSeq.current) return;
      const page = inventoryPage(res?.data);
      setRows((prev) => (from ? [...prev, ...page.items] : page.items));
      setNextCursor(page.nextCursor);
      setTableError(null);
    } catch (e) {
      if (myReq !== reqSeq.current) return;
      setTableError(e?.message || 'Could not read the allocation ledger.');
    } finally {
      if (myReq === reqSeq.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  // Debounced filter -> URL sync -> restart the walk at page one. The request
  // carries no filter keys today because no filter struct exists server-side, and
  // a key the backend ignores is a filter that silently does nothing; the reset is
  // still required, because filtering an arbitrary prefix of a keyset walk answers
  // "does anything match?" with "nothing on the pages you happened to load".
  useEffect(() => {
    setRows([]);
    setNextCursor('');
    const t = setTimeout(() => {
      const params = {};
      Object.entries({ pool, account, region, source, search }).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      setSearchParams(params, { replace: true });
      load('');
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [pool, account, region, source, search, load, setSearchParams]);

  // Pool id -> scope id. GetIpamAddressHistory needs BOTH a CIDR and a scope, and
  // an allocation row carries only its pool — so the scope is resolved from the
  // pool tree, whose ScopeID comes from Prism's ledger rather than from a re-read
  // of describe-ipam-pools (which answers with a null scope id even to the pool's
  // own owner). A pool the tree has not loaded resolves to nothing, and the drawer
  // says so rather than spending a live AWS call on a request the backend will
  // reject with a 400.
  const poolIndex = useMemo(() => {
    const byId = {};
    pools.forEach((p) => {
      if (p?.poolId) byId[p.poolId] = p;
    });
    return byId;
  }, [pools]);

  const poolLabel = useCallback((poolId) => {
    const p = poolIndex[poolId];
    if (!p) return poolId || '—';
    return p.description ? `${p.description}` : poolId;
  }, [poolIndex]);

  // Cold start is discriminated by the SERVER's config.firstDiscoveryAt AND by
  // the status. `firstDiscoveryAt == null` on its own means "no sweep has ever
  // landed", which is true both of a tenant waiting on AWS and of a tenant that
  // never built an IPAM at all. Only the first of those is a cold start; the
  // second gets the card below, which names the state it is actually in.
  //
  // The two are mutually exclusive and leave no gap: cold start requires a LIVE
  // config, which is the first thing ipamHasNothingToShow rules out.
  //
  // AND `noIpamYet` IS GATED ON THE READ OUTCOME, not merely on `error`. The old
  // predicate held only while every rejection path happens to leave a truthy
  // message in a field the ledger read also writes; the branch that inherited the
  // case where one does not is a claim about the customer's setup.
  const configUnread = contextLoaded && (error || configRead === 'failed') && !config;
  const noIpamYet = contextLoaded && configRead === 'ok' && !error
    && ipamHasNothingToShow(config) && !ipamStatusUnread(config);
  // The blank-status arm, kept apart from `noIpamYet` so the two never share a
  // card: one is a state Prism read, the other is a read that produced nothing.
  const livenessUnread = contextLoaded && configRead === 'ok' && !error && ipamStatusUnread(config);
  // An IPAM can outlive the config row's status — see ipamResidualIpamCard. Null
  // whenever Prism holds no evidence that one is still there.
  const residualCard = ipamResidualIpamCard(config, 'allocations');
  const noteSeverity = ipamConfigNoteSeverity(config?.note, config?.noteSource);
  const noteTitle = ipamConfigNoteTitle(config?.note, config?.noteSource);
  const coldStart = !!config && ipamIsLive(config.status) && !config.firstDiscoveryAt;

  const liveMembers = useMemo(() => members.filter(isLiveMember), [members]);

  const broken = useMemo(
    () => liveMembers.filter((m) => m?.resourceDiscoveryStatus === 'not-found'),
    [liveMembers],
  );
  const discovering = useMemo(
    () => liveMembers.filter((m) => m?.resourceDiscoveryStatus !== 'not-found'),
    [liveMembers],
  );
  const associations = useMemo(
    () => liveMembers
      .filter((m) => m?.resourceDiscoveryStatus)
      .map((m) => ({
        accountId: m.accountId,
        region: m.discoveryRegion || config?.homeRegion || '',
        status: m.resourceDiscoveryStatus,
      })),
    [liveMembers, config],
  );

  const regionCount = parseRegions(config?.operatingRegions).length;

  const checkNow = useCallback(async () => {
    setChecking(true);
    try {
      await loadContext({ quiet: true });
      await load('');
    } finally {
      setChecking(false);
    }
  }, [loadContext, load]);

  // 30 s, not the shared 3 s POLL_INTERVAL_MS, and keyed on the primitive.
  useEffect(() => {
    if (!coldStart) return undefined;
    const id = setInterval(() => {
      if (pollFailuresRef.current >= MAX_POLL_FAILURES) {
        clearInterval(id);
        return;
      }
      checkNow();
    }, IPAM_DISCOVERY_POLL_MS);
    return () => clearInterval(id);
  }, [coldStart, checkNow]);

  // Client-side over the rows fetched so far — /inventory/allocations accepts
  // cursor and limit and nothing else. The caption under the table says so
  // whenever more pages remain.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (pool && r?.ipamPoolId !== pool) return false;
      if (account && r?.resourceOwnerId !== account) return false;
      if (region && r?.resourceRegion !== region) return false;
      if (source && r?.allocationSource !== source) return false;
      if (!q) return true;
      return [r?.cidr, r?.resourceId, r?.allocationId, r?.description, r?.ipamPoolId]
        .some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [rows, pool, account, region, source, search]);

  const poolOptions = useMemo(() => optionsFrom(rows, 'ipamPoolId', pool), [rows, pool]);
  const accountOptions = useMemo(() => optionsFrom(rows, 'resourceOwnerId', account), [rows, account]);
  const regionOptions = useMemo(() => optionsFrom(rows, 'resourceRegion', region), [rows, region]);
  const sourceOptions = useMemo(() => optionsFrom(rows, 'allocationSource', source), [rows, source]);

  const blockingCount = useMemo(
    () => filtered.filter((r) => String(r?.allocationSource || '').toLowerCase() !== 'manual').length,
    [filtered],
  );

  const filtersActive = !!(pool || account || region || source || search);
  const lastRefreshed = formatTime(config?.lastDiscoveryRefreshAt);

  // "0 WOULD BLOCK TEARDOWN", IN SUCCESS GREEN, OVER A CACHE NOBODY HAD READ.
  // The count is over the LOADED and FILTERED rows, so it was drawn as a clean
  // all-clear while the table underneath said "Prism has not read your pool
  // allocations yet", while a cursor remained, and while a filter was narrowing
  // the very rows being counted. Both siblings had already been fixed —
  // IpamAddressSpace separates assessed from not-yet-assessed, IpamPublicIPs goes
  // neutral at zero — and the rule lives in ipamCopy.js so a fourth screen cannot
  // re-derive it differently.
  const blockingChip = useMemo(() => ipamBlockingAllocationsChip({
    blockingCount,
    cacheReadAt: config?.poolAllocationsReadAt,
    morePagesRemain: !!nextCursor,
    filtersActive,
  }), [blockingCount, config?.poolAllocationsReadAt, nextCursor, filtersActive]);

  // THE UNFILTERED ZERO STATE, AND WHAT LICENSES ITS CLAIM. "AWS reported no pool
  // allocations" may only be said once config.poolAllocationsReadAt exists — the
  // server's record of a refresh in which the pool list AND every pool's
  // allocations were read. firstDiscoveryAt cannot stand in for it: it is one
  // aggregate over five caches, so a tenant with discovered CIDRs and an unread
  // allocation cache clears the cold-start panel and lands straight here.
  const zeroState = useMemo(() => ipamAllocationsZeroState({
    cacheReadAt: config?.poolAllocationsReadAt,
    morePagesRemain: !!nextCursor,
    accountCount: discovering.length,
    regionCount,
    someAccountsBroken: broken.length > 0,
    lastRefreshed,
  }), [config, nextCursor, discovering.length, regionCount, broken.length, lastRefreshed]);

  const header = (
    <PortalPageHeader
      icon={<AccountTreeOutlinedIcon sx={{ fontSize: 24 }} />}
      title="Allocations"
      subtitle="Every CIDR IPAM handed out of a pool, across every pool and every account"
      color={IPAM_COLOR}
    />
  );

  // MUST PRECEDE EVERYTHING ELSE. A rejection from GET /config leaves `config`
  // null, and without this the page falls through to a branch that states what
  // the customer's setup IS — while the one read that would have established it
  // is the read that failed. Same guard as IpamPublicIPs.js and IpamAccounts.js.
  if (configUnread) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState
            error={error || 'Prism could not read this customer\u2019s VPC IPAM configuration.'}
            onRetry={() => { loadContext(); load(''); }}
          />
        </motion.div>
      </motion.div>
    );
  }

  // ── THE NOT-LIVE GATE, ABOVE THE COVERAGE BANNER RATHER THAN BESIDE IT ─────
  //
  // This used to sit BELOW the "N of M accounts are no longer being discovered"
  // warning, so a present-tense coverage claim about AWS stacked on top of "VPC
  // IPAM is not enabled yet". IpamPublicIPs.js has always returned early here;
  // this now matches it.
  if (noIpamYet || livenessUnread) {
    const meta = livenessUnread
      ? { title: IPAM_LIVENESS_UNREAD_TITLE, description: IPAM_LIVENESS_UNREAD_BODY }
      : (residualCard || notLiveMeta(config?.status));
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalEmptyState
              icon={<AccountTreeOutlinedIcon sx={{ fontSize: 28 }} />}
              title={meta.title}
              description={meta.description}
              color={IPAM_COLOR}
              action={(
                <Button
                  variant="contained"
                  component={RouterLink}
                  to={IPAM_ROOT_ROUTE}
                  sx={{ fontFamily: FONT_BODY, textTransform: 'none', backgroundColor: IPAM_COLOR }}
                >
                  Go to VPC IPAM
                </Button>
              )}
            />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>{header}</motion.div>

      {/* THE DEGRADATION NOTE, ON THE PAGE THE STALE ROWS ARE ON. `config.note`
          carries the unattributed degradation channel — "AWS no longer reports
          IPAM %s … no pool can be allocated from" — which rendered as
          severity="info" on the dashboard and on no other page. */}
      {noteSeverity && (
        <motion.div variants={sectionVariants}>
          <Alert severity={noteSeverity} sx={{ mb: 3, fontFamily: FONT_BODY }}>
            {noteTitle && (
              <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>{noteTitle}</AlertTitle>
            )}
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>{config.note}</Typography>
          </Alert>
        </motion.div>
      )}

      {/* The coverage gap is ALWAYS drawn as a warning ABOVE the results
          (OrphanScanPage.js:573-600) — the table below looks perfectly healthy
          precisely because whole accounts are missing from it. */}
      {broken.length > 0 && (
        <motion.div variants={sectionVariants}>
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3, fontFamily: FONT_BODY }}>
            {/* The denominator is the LIVE members, never `members.length`: a
                removed account is not a coverage gap, and counting its tombstone
                here made this sentence disagree with the Accounts page's own
                enrolled count on the same data. */}
            <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
              {broken.length} of {liveMembers.length}{' '}
              {plural(liveMembers.length, 'account is', 'accounts are')} no longer being discovered.
            </AlertTitle>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', mb: 1 }}>
              Their resource discovery was deleted or their RAM share was revoked, so anything
              in {plural(broken.length, 'that account', 'those accounts')} is missing from every
              table on this page, however clean it looks.
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
              {broken.map((m) => (
                <Chip
                  key={m.accountId}
                  size="small"
                  label={`${m.accountId}${m.discoveryRegion ? ` · ${m.discoveryRegion}` : ''}`}
                  sx={statusChipSx('error')}
                />
              ))}
            </Stack>
            <Button
              size="small"
              component={RouterLink}
              to={ACCOUNTS_ROUTE}
              sx={{ fontFamily: FONT_BODY, textTransform: 'none' }}
            >
              Review on the Accounts page
            </Button>
          </Alert>
        </motion.div>
      )}

      {/* THE distinction. Stated on the page, not only in the design doc. */}
      <motion.div variants={sectionVariants}>
        <Alert severity="info" sx={{ mb: 3, fontFamily: FONT_BODY }}>
          <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
            This is what IPAM <b>handed out</b>, not everything it can see.
          </AlertTitle>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>
            Every row below came out of an IPAM pool.{' '}
            <RouterLink to={ADDRESS_SPACE_ROUTE} style={{ color: CK.blue }}>
              Address space
            </RouterLink>{' '}
            lists every CIDR IPAM discovered on a real resource, pool or no pool. A VPC created
            outside a pool appears there and not here — that difference is the compliance story,
            and it is address space nothing is planning around. These are not two views of one
            dataset.
          </Typography>
        </Alert>
      </motion.div>

      {/* The CONTEXT read's error. It survives a successful ledger page, which is
          the whole point of splitting the two: a config Prism could not read stays
          reported for as long as it stays unread. */}
      {error && (
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => { loadContext(); load(''); }} sx={{ mb: 3 }} />
        </motion.div>
      )}

        {/* === Filters === */}
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              useFlexGap
              flexWrap="wrap"
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Pool</InputLabel>
                <Select value={pool} label="Pool" onChange={(e) => setPool(e.target.value)}>
                  <MenuItem value="">All pools</MenuItem>
                  {poolOptions.map((o) => (
                    <MenuItem key={o} value={o}>{poolLabel(o)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Owner account</InputLabel>
                <Select
                  value={account}
                  label="Owner account"
                  onChange={(e) => setAccount(e.target.value)}
                >
                  <MenuItem value="">All accounts</MenuItem>
                  {accountOptions.map((o) => (
                    <MenuItem key={o} value={o} sx={{ fontFamily: FONT_MONO }}>{o}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Region</InputLabel>
                <Select value={region} label="Region" onChange={(e) => setRegion(e.target.value)}>
                  <MenuItem value="">All regions</MenuItem>
                  {regionOptions.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 175 }}>
                <InputLabel>Source</InputLabel>
                <Select value={source} label="Source" onChange={(e) => setSource(e.target.value)}>
                  <MenuItem value="">All sources</MenuItem>
                  {sourceOptions.map((o) => (
                    <MenuItem key={o} value={o}>{sourceMeta(o).label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Search CIDR, resource or description"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ flexGrow: 1, minWidth: 220 }}
              />
            </Stack>
          </Paper>
        </motion.div>

        {/* === The ledger === */}
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalSectionHeader
              icon={<AccountTreeOutlinedIcon sx={{ fontSize: 20 }} />}
              title="Pool allocations"
              subtitle="Includes allocations member accounts made themselves out of pools shared to them. Prism cannot release those, but they still block deleting the pool."
              color={IPAM_COLOR}
              action={(
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip
                    size="small"
                    label={`${filtered.length} ${plural(filtered.length, 'allocation', 'allocations')}`}
                    sx={statusChipSx('info')}
                  />
                  <Tooltip title={blockingChip.hint}>
                    <Chip
                      size="small"
                      label={blockingChip.label}
                      sx={statusChipSx(blockingChip.intent)}
                    />
                  </Tooltip>
                </Stack>
              )}
            />

            {/* The FEED's own error, beside the feed. It never touches `error`
                above, so a failing ledger page cannot clear a failing config
                read and cannot borrow its retry either. */}
            {tableError && (
              <PortalErrorState error={tableError} onRetry={() => load('')} sx={{ mb: 2 }} />
            )}

            {loading && rows.length === 0 ? (
              <PortalLoading label="Loading the allocation ledger…" color={IPAM_COLOR} />
            ) : coldStart && rows.length === 0 ? (
              <IpamDiscoveryColdStart
                associations={associations}
                enabledAt={config?.costAcknowledgedAt || config?.createdAt}
                // lastDiscoveryRefreshAt is the SERVER's sweep time and belongs
                // in lastSweepAt, not in lastCheckedAt: passing it as "last
                // checked" made a tenant Prism had never read look freshly read.
                lastSweepAt={config?.lastDiscoveryRefreshAt}
                onCheck={checkNow}
                checking={checking}
              />
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 1180 }}>
                  <TableHead>
                    <TableRow sx={tableHeadRowSx}>
                      <TableCell sx={headCellSx}>CIDR</TableCell>
                      <TableCell sx={headCellSx}>Pool</TableCell>
                      <TableCell sx={headCellSx}>Source</TableCell>
                      <TableCell sx={headCellSx}>Held by</TableCell>
                      <TableCell sx={headCellSx}>Owner account</TableCell>
                      <TableCell sx={headCellSx}>Region</TableCell>
                      <TableCell sx={headCellSx}>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((r, index) => {
                      const meta = sourceMeta(r.allocationSource);
                      const isResourceBacked =
                        String(r.allocationSource || '').toLowerCase() !== 'manual';
                      const scopeId = poolIndex[r.ipamPoolId]?.scopeId || '';
                      return (
                        <TableRow
                          key={r.id || `${r.ipamPoolId}/${r.allocationId}`}
                          hover
                          sx={rowSx(index)}
                        >
                          <TableCell sx={cidrCellSx}>
                            <Tooltip title="View this CIDR’s address history (a live read from AWS)">
                              <Box
                                component="button"
                                type="button"
                                onClick={() => setHistoryRow({ cidr: r.cidr, scopeId })}
                                sx={{
                                  font: 'inherit',
                                  color: CK.navy,
                                  background: 'none',
                                  border: 'none',
                                  p: 0,
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  textDecorationStyle: 'dotted',
                                  textUnderlineOffset: '3px',
                                }}
                              >
                                {r.cidr || '—'}
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            <RouterLink
                              to={`${POOLS_ROUTE}/${encodeURIComponent(r.ipamPoolId || '')}`}
                              style={{ color: CK.blue, textDecoration: 'none' }}
                            >
                              {poolLabel(r.ipamPoolId)}
                            </RouterLink>
                            <Box sx={{ fontFamily: FONT_MONO, color: CK.muted, fontSize: '0.72rem', wordBreak: 'break-all' }}>
                              {r.ipamPoolId || '—'}
                            </Box>
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Tooltip title={meta.hint}>
                              <Chip size="small" label={meta.label} sx={statusChipSx(meta.intent)} />
                            </Tooltip>
                          </TableCell>
                          {/* The owning resource and its account are on the row, not
                              behind a click: this pair is the named blocker teardown
                              reports, and an operator who first meets it inside a
                              blocked teardown has met it too late. */}
                          <TableCell sx={bodyCellSx}>
                            {isResourceBacked ? (
                              <>
                                <Box sx={{ fontFamily: FONT_MONO, fontSize: '0.8125rem', wordBreak: 'break-all' }}>
                                  {r.resourceId || 'unknown resource'}
                                </Box>
                                <Box sx={{ color: CK.muted, fontSize: '0.75rem' }}>
                                  {[formatStatusLabel(r.resourceType), r.resourceOwnerId]
                                    .filter(Boolean).join(' · ') || '—'}
                                </Box>
                              </>
                            ) : (
                              <Tooltip title="A manual reservation is backed by no AWS resource, so nothing has to be deleted before it can be released.">
                                <Box component="span" sx={{ color: CK.muted, cursor: 'help' }}>
                                  no resource
                                </Box>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, fontFamily: FONT_MONO, whiteSpace: 'nowrap' }}>
                            {r.resourceOwnerId || '—'}
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>
                            {r.resourceRegion || '—'}
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-word' }}>
                            {r.description || (
                              <Box component="span" sx={{ color: CK.muted }}>—</Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* A FAILED READ IS NOT A ZERO. The scoped empty state below
                        says "AWS reported no pool allocations", which is a claim
                        Prism cannot make when the ledger did not answer — including
                        when it answered in an unrecognised shape. That confident
                        zero is precisely what the old tolerant reader manufactured,
                        so here the error card above is left to speak for itself. */}
                    {!loading && tableError && filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.muted }}>
                            This table is empty because the read failed, not because nothing has
                            been allocated. See the error above.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}

                    {!loading && !tableError && filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                          {filtersActive ? (
                            <PortalEmptyState
                              icon={<AccountTreeOutlinedIcon sx={{ fontSize: 28 }} />}
                              title="No allocations match these filters"
                              description={
                                nextCursor
                                  ? 'Filters apply to the rows loaded so far. Clear a filter, or load more rows and search again.'
                                  : 'Clear a filter to widen the search.'
                              }
                              color={IPAM_COLOR}
                              py={2}
                            />
                          ) : (
                            // A SCOPED zero. And on THIS page an honest zero has a
                            // second, entirely legitimate reading the Address Space
                            // page does not have: pools may exist with nothing
                            // allocated out of them yet, which is a normal state and
                            // not a gap.
                            <PortalEmptyState
                              icon={<AccountTreeOutlinedIcon sx={{ fontSize: 28 }} />}
                              title={zeroState.title}
                              description={zeroState.description}
                              color={IPAM_COLOR}
                              py={2}
                              action={(
                                <Button
                                  size="small"
                                  component={RouterLink}
                                  to={ADDRESS_SPACE_ROUTE}
                                  sx={{ fontFamily: FONT_BODY, textTransform: 'none' }}
                                >
                                  View discovered address space
                                </Button>
                              )}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {(nextCursor || filtersActive) && !coldStart && (
              <Box
                sx={{
                  mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 2, flexWrap: 'wrap',
                }}
              >
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted }}>
                  {nextCursor
                    ? `Showing ${filtered.length} of ${rows.length} rows loaded. More pages remain — filters apply only to what has been loaded.`
                    : `Showing ${filtered.length} of ${rows.length} rows. Everything has been loaded.`}
                </Typography>
                {nextCursor && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => load(nextCursor)}
                    disabled={loadingMore}
                    sx={{ fontFamily: FONT_BODY, textTransform: 'none' }}
                  >
                    {loadingMore ? 'Loading…' : 'Load more'}
                  </Button>
                )}
              </Box>
            )}
          </Paper>
        </motion.div>

        <motion.div variants={sectionVariants}>
          <Typography
            sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted, mt: 2 }}
          >
            <HistoryIcon sx={{ fontSize: 14, verticalAlign: '-2px', mr: 0.5 }} />
            Click any CIDR to see which resources have held it. That lookup goes straight to AWS
            and is not cached — AWS destroys the history irreversibly when the IPAM is deleted.
          </Typography>
        </motion.div>

      <IpamHistoryDrawer
        open={!!historyRow}
        onClose={() => setHistoryRow(null)}
        cidr={historyRow?.cidr}
        scopeId={historyRow?.scopeId}
      />
    </motion.div>
  );
}
